import type { ChangeEvent } from 'react';
import React, { useState } from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  HasErrorFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import {
  CheckboxWithLabel,
  CloseButton,
  InputField,
} from '@noths/polaris-client-ribbons-design-system';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';

import { CheckboxGroup } from 'src/components/molecules/CheckboxGroup/CheckboxGroup';
import * as groupStyles from 'src/components/molecules/styles/Group.styles';
import {
  HIGH_STREET_STORES_OPTIONS,
  INITIAL_ONLINE_STORES_OPTIONS,
  SUPER_MARKETS_OPTIONS,
} from 'src/components/organisms/Signup/constants/aboutYourBusinessOptions';
import {
  ONLINE_STORE_ALREADY_EXISTS_ERROR,
  ONLINE_STORE_MUST_BE_PROVIDED_ERROR,
  WEBLINK_MUST_BE_PROVIDED_ERROR,
} from 'src/components/organisms/Signup/constants/copy';
import { ONLINE_STORE_WEBLINK_PREFIX_TESTID } from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import * as styles from 'src/styles/styles';
import type { OnlineStore, Store } from 'src/types/business';
import type { RegistrationForm } from 'src/types/registration';
import { AddOtherOnlineStore } from './AddOtherOnlineStore';

interface Props {
  clearError: (errorName: string) => void;
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValues: RegistrationForm;
  hasError: HasErrorFn;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

type RegistrationFormField = keyof RegistrationForm;
type RegistrationFormFieldValue = RegistrationForm[RegistrationFormField];

export const getDynamicOnlineStoreOptionName = (id: string) =>
  `salesChannels.onlineStores.storesUsed.${id}.url`;

const generateOnlineStoreOptions = (onlineStores: OnlineStore[]) => {
  const options = [...INITIAL_ONLINE_STORES_OPTIONS];
  onlineStores.forEach((selectedOnlineStore) => {
    if (
      !INITIAL_ONLINE_STORES_OPTIONS.find(
        (initialStore) => initialStore.id === selectedOnlineStore.id,
      )
    ) {
      options.push({
        id: selectedOnlineStore.id,
        labelText: selectedOnlineStore.name,
        value: selectedOnlineStore.name,
      });
    }
  });
  return options;
};

export const SalesChannels = ({
  clearError,
  deregisterValidation,
  errors,
  formValues,
  hasError,
  onBlur,
  onFormChange,
  registerValidation,
  setFormValue,
}: Props) => {
  const [showAddOnlineStore, setShowAddOnlineStore] = useState(false);

  const onlineStoresOptions = generateOnlineStoreOptions(
    formValues.salesChannels.onlineStores.storesUsed,
  );

  const updateSalesChannels = (
    storeNames: unknown[],
    channelName: keyof RegistrationForm['salesChannels'],
  ) => {
    const stores = (
      channelName === 'onlineStores' ? storeNames : storeNames.map((name) => ({ name }))
    ) as Store[];

    setFormValue(
      `salesChannels.${channelName}.storesUsed` as RegistrationFormField,
      stores as unknown as RegistrationFormFieldValue,
    );
  };

  const registerOnlineStoreValidation = (id: string) => {
    const associatedInputFieldName = getDynamicOnlineStoreOptionName(id);
    registerValidation(associatedInputFieldName, {
      validate: {
        value: (_val, state) => {
          const foundOnlineStore = state.salesChannels.onlineStores.storesUsed.find(
            (onlineStore) => onlineStore.id === id,
          );
          if (foundOnlineStore) {
            return foundOnlineStore.url.trim() === '';
          }
          /* istanbul ignore next */
          return false;
        },
        message: WEBLINK_MUST_BE_PROVIDED_ERROR,
      },
    });
  };

  const handleOnlineStoresSalesChannels = (
    e: ChangeEvent<HTMLInputElement>,
    id: string,
    checkboxValue: string,
  ) => {
    const associatedInputFieldName = getDynamicOnlineStoreOptionName(id);
    const { storesUsed } = formValues.salesChannels.onlineStores;

    if (hasError('salesChannels.onlineStores.uses')) {
      clearError('salesChannels.onlineStores.uses');
    }

    if (e.target.checked) {
      registerOnlineStoreValidation(id);

      setFormValue(
        'salesChannels.onlineStores.storesUsed' as keyof RegistrationForm,
        storesUsed.concat({
          id,
          name: checkboxValue,
          url: '',
        }) as unknown as RegistrationForm[keyof RegistrationForm],
      );
    } else {
      const filteredOptions = storesUsed.filter(({ id: storeId }) => storeId !== id);

      deregisterValidation(associatedInputFieldName);
      setFormValue(
        'salesChannels.onlineStores.storesUsed' as keyof RegistrationForm,
        filteredOptions as unknown as RegistrationFormFieldValue,
      );
    }
  };

  const handleSalesChannelsChange = R.curry(
    (channel: keyof RegistrationForm['salesChannels']) => (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setFormValue(
          `salesChannels.${channel}` as keyof RegistrationForm,
          {
            uses: false,
            storesUsed: [],
          } as unknown as RegistrationForm[keyof RegistrationForm],
        );
      } else {
        onFormChange(e);
      }
    },
  );

  return (
    <>
      <fieldset css={[groupStyles.borderless, styles.grouped]}>
        <legend>Where else are your products available to sell?</legend>
        <p css={styles.paragraphStyle}>
          We know that lots of our Partners sell through other channels and with other businesses.
          Please add below where your products are available for sale. This helps us get an idea
          about the readiness of your business and can help speed up the assessment of your
          application
        </p>
        <CheckboxWithLabel
          checked={formValues.salesChannels.supermarkets.uses}
          css={styles.checkboxWrapper}
          key="supermarkets"
          label="Supermarkets"
          name="salesChannels.supermarkets.uses"
          onChange={handleSalesChannelsChange('supermarkets')}
        />
        {formValues.salesChannels.supermarkets.uses && (
          <div css={styles.growIn}>
            <CheckboxGroup
              accessibleLabel="Supermarkets"
              additionalCss={[styles.grouped, styles.indentedCheckboxes]}
              checkboxData={SUPER_MARKETS_OPTIONS}
              hideLabel
              name="supermarkets"
              onChange={updateSalesChannels}
              value={formValues.salesChannels.supermarkets.storesUsed.map(
                (store: Store) => store.name,
              )}
            />
          </div>
        )}
        <CheckboxWithLabel
          checked={formValues.salesChannels.onlineStores.uses}
          css={styles.checkboxWrapper}
          error={errors['salesChannels.onlineStores.uses'] as string | undefined}
          key="onlineStores"
          label="Online stores"
          name="salesChannels.onlineStores.uses"
          onChange={(e) => {
            if (e.target.checked) {
              registerValidation('salesChannels.onlineStores.uses', {
                validate: {
                  value: (val, state) =>
                    state.salesChannels.onlineStores.uses &&
                    !state.salesChannels.onlineStores.storesUsed.length,
                  message: ONLINE_STORE_MUST_BE_PROVIDED_ERROR,
                },
              });
            } else {
              deregisterValidation('salesChannels.onlineStores.uses');
              onlineStoresOptions.forEach((_onlineStore, index) => {
                deregisterValidation(`salesChannels.onlineStores.storesUsed.${index}.url`);
              });
            }
            handleSalesChannelsChange('onlineStores')(e);
          }}
        />
        {formValues.salesChannels.onlineStores.uses && (
          <div css={styles.growIn}>
            {onlineStoresOptions.map(({ id, labelText, value: checkboxValue }) => (
              <div css={styles.onlineStoresColumns} key={checkboxValue}>
                <CheckboxWithLabel
                  checked={formValues.salesChannels.onlineStores.storesUsed.some(
                    ({ id: storeId }) => storeId === id,
                  )}
                  css={[styles.grouped, styles.indentedCheckboxes, styles.onlineStoreCheckbox]}
                  label={labelText}
                  name={`salesChannels.onlineStores.storesUsed.${id}.name`}
                  onChange={(e) => handleOnlineStoresSalesChannels(e, id, checkboxValue)}
                  required
                  value={checkboxValue}
                />
                <InputField
                  css={styles.webLinkField}
                  data-testid={`${ONLINE_STORE_WEBLINK_PREFIX_TESTID}-${labelText}`}
                  disabled={
                    !formValues.salesChannels.onlineStores.storesUsed.some(
                      ({ id: storeId }) => storeId === id,
                    )
                  }
                  error={errors[getDynamicOnlineStoreOptionName(id)] as string | undefined}
                  id={`online-store-${checkboxValue}-weblink`}
                  label={`${labelText} weblink`}
                  name={getDynamicOnlineStoreOptionName(id)}
                  onBlur={onBlur}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    const filteredOnlineStores =
                      formValues.salesChannels.onlineStores.storesUsed.filter(
                        (store) => store.id !== id,
                      );
                    const onlineStores = filteredOnlineStores.concat([
                      {
                        id,
                        name: checkboxValue,
                        url: value,
                      },
                    ]);
                    setFormValue(
                      `salesChannels.onlineStores.storesUsed` as RegistrationFormField,
                      onlineStores,
                    );
                  }}
                  placeholder="Weblink"
                  required
                  value={
                    formValues.salesChannels.onlineStores.storesUsed.find(
                      (store) => store.id === id,
                    )?.url || ''
                  }
                />

                <CloseButton
                  aria-label={`Delete online store ${labelText}`}
                  css={styles.onlineStoreDeleteButton}
                  id={`delete-online-store-${checkboxValue}`}
                  onClick={() => {
                    const updatedStores = formValues.salesChannels.onlineStores.storesUsed.filter(
                      ({ id: storeId }) => storeId !== id,
                    );
                    updateSalesChannels(updatedStores, 'onlineStores');
                  }}
                  size="small"
                />
              </div>
            ))}
            <div css={styles.onlineStoresColumns}>
              <CheckboxWithLabel
                checked={showAddOnlineStore}
                css={[styles.grouped, styles.indentedCheckboxes, styles.onlineStoreCheckbox]}
                label="Other"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setShowAddOnlineStore(e.target.checked)
                }
              />
            </div>
            {showAddOnlineStore && (
              <div css={styles.onlineStoreDialogue}>
                <AddOtherOnlineStore
                  css={styles.onlineStoreGrid}
                  name="additionalOnlineStores"
                  onAdd={(name, url) => {
                    const newId = uuidv4();
                    const updatedOnlineStores = [
                      ...formValues.salesChannels.onlineStores.storesUsed,
                      { id: newId, name, url },
                    ];
                    clearError('salesChannels.onlineStores.uses');
                    updateSalesChannels(updatedOnlineStores, 'onlineStores');
                    setShowAddOnlineStore(false);
                    registerOnlineStoreValidation(newId);
                  }}
                  onCancel={() => setShowAddOnlineStore(false)}
                  storeNamePlaceholder="Online store name"
                  validate={({ storeName }) => {
                    const hasExistingStore = formValues.salesChannels.onlineStores.storesUsed.some(
                      (st) => st.name.toLowerCase().trim() === storeName.toLowerCase().trim(),
                    );

                    return hasExistingStore
                      ? { storeName: ONLINE_STORE_ALREADY_EXISTS_ERROR }
                      : null;
                  }}
                  valuePlaceholder="Weblink"
                />
              </div>
            )}
          </div>
        )}
        <CheckboxWithLabel
          checked={formValues.salesChannels.highstreetStores.uses}
          css={styles.checkboxWrapper}
          key="highstreetStores"
          label="High Street stores"
          name="salesChannels.highstreetStores.uses"
          onChange={handleSalesChannelsChange('highstreetStores')}
        />
        {formValues.salesChannels.highstreetStores.uses && (
          <div css={styles.growIn}>
            <CheckboxGroup
              accessibleLabel="High street stores"
              additionalCss={[styles.grouped, styles.indentedCheckboxes]}
              checkboxData={HIGH_STREET_STORES_OPTIONS}
              hideLabel
              name="highstreetStores"
              onChange={updateSalesChannels}
              value={formValues.salesChannels.highstreetStores.storesUsed.map(
                ({ name }: Store) => name,
              )}
            />
          </div>
        )}
        <CheckboxWithLabel
          checked={formValues.salesChannels.boutiques.uses}
          css={styles.checkboxWrapper}
          key="boutiques"
          label="Small boutiques"
          name="salesChannels.boutiques.uses"
          onChange={onFormChange}
        />
      </fieldset>
    </>
  );
};
